import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getDashboard, downloadPDF, deleteInvoice } from "../api/invoices";
import { useAuth } from "../context/AuthContext";

const fmt = (n) => "₹" + (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 });
const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const STATUS_COLORS = {
  paid: { bg: "#d1fae5", color: "#065f46" },
  sent: { bg: "#dbeafe", color: "#1e40af" },
  draft: { bg: "#f3f4f6", color: "#6b7280" },
  overdue: { bg: "#fee2e2", color: "#991b1b" },
  cancelled: { bg: "#fef3c7", color: "#92400e" },
};

export default function Dashboard() {
  const navigate = useNavigate();
   const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
const [mobileMenu, setMobileMenu] = useState(false);
  useEffect(() => {
    setMounted(true);
    getDashboard()
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (id, invoiceNumber) => {
    const res = await downloadPDF(id);
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${invoiceNumber}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await deleteInvoice(id);
    const r = await getDashboard();
    setData(r.data);
  };
 const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate("/");
  };
  const stats = data?.stats || {};
  const recent = data?.recent || [];
  const usagePercent = stats.limit === Infinity ? 0 : Math.round((stats.invoiceCount / stats.limit) * 100);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --bg: #f5f4f0;
          --surface: #ffffff;
          --border: #e8e6e0;
          --ink: #1a1a24;
          --ink-2: #3d3d4f;
          --muted: #8a8a9a;
          --accent: #2d2b69;
          --accent-light: #eeedf8;
          --green: #059669;
          --green-bg: #d1fae5;
          --red: #dc2626;
          --red-bg: #fee2e2;
          --yellow: #d97706;
          --yellow-bg: #fef3c7;
        }
        body { font-family: 'Sora', sans-serif; background: var(--bg); color: var(--ink); }

        .shell { display: flex; min-height: 100vh; }

        /* Sidebar */
        .sidebar {
          width: 240px; flex-shrink: 0; background: var(--accent);
          display: flex; flex-direction: column;
          position: fixed; top: 0; left: 0; height: 100vh;
          opacity: 0; transform: translateX(-16px);
          transition: opacity 0.4s ease, transform 0.4s ease;
          z-index: 100;
        }
        .sidebar.in { opacity: 1; transform: translateX(0); }
        .sidebar-logo { padding: 28px 55px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); }
        .sidebar-logo .mark { font-size: 22px; font-weight: 700; color: white; letter-spacing: -0.5px; }
        .sidebar-logo .sub { font-size: 10px; color: rgba(255,255,255,0.4); letter-spacing: 1.5px; text-transform: uppercase; margin-top: 2px; }
        .sidebar-nav { padding: 20px 12px; flex: 1; }
        .nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: 10px; margin-bottom: 2px;
          font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.6);
          cursor: pointer; transition: all 0.15s; text-decoration: none;
        }
        .nav-item:hover, .nav-item.active { background: rgba(255,255,255,0.12); color: white; }
        .nav-icon { width: 18px; text-align: center; font-size: 14px; }
        .sidebar-footer { padding: 16px 12px; border-top: 1px solid rgba(255,255,255,0.1); }
        .user-row { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 10px; }
        .user-avatar { width: 32px; height: 32px; border-radius: 8px; background: rgba(255,255,255,0.2); display: grid; place-items: center; font-size: 13px; font-weight: 600; color: white; overflow: hidden; flex-shrink: 0; }
        .user-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .user-name { font-size: 12px; font-weight: 600; color: white; }
        .user-plan { font-size: 10px; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 1px; }
        .logout-btn { width: 100%; margin-top: 8px; padding: 8px; background: rgba(255,255,255,0.08); border: none; border-radius: 8px; color: rgba(255,255,255,0.5); font-family: 'Sora', sans-serif; font-size: 12px; cursor: pointer; transition: all 0.15s; }
        .logout-btn:hover { background: rgba(255,80,80,0.2); color: #fca5a5; }

        /* Main */
        .main { margin-left: 240px; flex: 1; padding: 40px; opacity: 0; transition: opacity 0.4s ease 0.2s; }
        .main.in { opacity: 1; }

        .page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; }
        .page-title { font-size: 24px; font-weight: 700; letter-spacing: -0.5px; }
        .page-sub { font-size: 13px; color: var(--muted); margin-top: 2px; }

        .btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 10px; font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; border: none; }
        .btn-primary { background: var(--accent); color: white; box-shadow: 0 2px 8px rgba(45,43,105,0.3); }
        .btn-primary:hover { background: #3d3b8a; transform: translateY(-1px); }
        .btn-ghost { background: transparent; color: var(--ink-2); border: 1px solid var(--border); }
        .btn-ghost:hover { background: var(--accent-light); border-color: var(--accent); color: var(--accent); }
        .btn-ai { background: linear-gradient(135deg, #7c3aed, #2d2b69); color: white; box-shadow: 0 2px 12px rgba(124,58,237,0.3); }
        .btn-ai:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(124,58,237,0.4); }

        /* Stats */
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px; }
        .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 20px 24px; }
        .stat-label { font-size: 11px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: var(--muted); margin-bottom: 10px; }
        .stat-value { font-size: 28px; font-weight: 700; letter-spacing: -1px; color: var(--ink); }
        .stat-sub { font-size: 12px; color: var(--muted); margin-top: 4px; }
        .stat-card.highlight { background: var(--accent); border-color: var(--accent); }
        .stat-card.highlight .stat-label { color: rgba(255,255,255,0.5); }
        .stat-card.highlight .stat-value { color: white; }
        .stat-card.highlight .stat-sub { color: rgba(255,255,255,0.5); }

        /* Usage bar */
        .usage-bar-wrap { margin-top: 10px; }
        .usage-bar-bg { background: rgba(255,255,255,0.15); border-radius: 999px; height: 4px; }
        .usage-bar-fill { height: 4px; border-radius: 999px; background: white; transition: width 0.6s ease; }

        /* Table */
        .table-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; overflow: hidden; }
        .table-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; border-bottom: 1px solid var(--border); }
        .table-title { font-size: 14px; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; }
        th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 600; letter-spacing: 0.8px; text-transform: uppercase; color: var(--muted); background: #fafaf8; border-bottom: 1px solid var(--border); }
        td { padding: 14px 16px; font-size: 13px; border-bottom: 1px solid var(--border); vertical-align: middle; }
        tr:last-child td { border-bottom: none; }
        tr:hover td { background: #fafaf8; }
        .invoice-num { font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 500; color: var(--accent); }
        .client-name { font-weight: 600; }
        .amount { font-weight: 700; font-family: 'JetBrains Mono', monospace; }
        .status-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
        .actions { display: flex; gap: 6px; }
        .action-btn { padding: 5px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: transparent; color: var(--muted); transition: all 0.15s; font-family: 'Sora', sans-serif; }
        .action-btn:hover { background: var(--accent-light); color: var(--accent); border-color: var(--accent); }
        .action-btn.danger:hover { background: var(--red-bg); color: var(--red); border-color: var(--red); }

        .empty-state { text-align: center; padding: 60px 24px; color: var(--muted); }
        .empty-icon { font-size: 40px; margin-bottom: 12px; }
        .empty-title { font-size: 15px; font-weight: 600; color: var(--ink-2); margin-bottom: 6px; }
        .empty-sub { font-size: 13px; margin-bottom: 20px; }

        .spinner { width: 32px; height: 32px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.6s linear infinite; margin: 60px auto; }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 900px) {
          .sidebar { display: none; }
          .main { margin-left: 0; padding: 20px; }
          .stats-grid { grid-template-columns: 1fr 1fr; }
      }
          
      /* =========================================
   RESPONSIVE DESIGN
========================================= */

/* Large Tablets */
@media (max-width: 1024px) {

  .main {
    margin-left: 0;
    padding: 24px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .page-header > div:last-child {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 10px !important;
  }

  .btn {
    justify-content: center;
  }

  .table-card {
    overflow-x: auto;
  }

  table {
    min-width: 760px;
  }
}


/* =========================================
   MOBILE MENU
========================================= */

.mobile-menu-btn {
  display: none;
}

.mobile-overlay {
  display: none;
}

@media (max-width: 768px) {

  /* Layout */
  .shell {
    display: block;
  }

  /* Mobile Button */
  .mobile-menu-btn {
    display: flex;
    position: fixed;
    top: 14px;
    left: 14px;
    z-index: 1001;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: var(--accent);
    color: white;
    font-size: 22px;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);
    cursor: pointer;
  }

  /* Overlay */
  .mobile-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 998;
    opacity: 0;
    pointer-events: none;
    transition: 0.25s;
  }

  .mobile-overlay.show {
    opacity: 1;
    pointer-events: auto;
  }

  /* Sidebar */
  .sidebar {
    position: fixed;
    top: 0;
    left: -280px;
    width: 260px;
    height: 100vh;
    z-index: 999;
    transition: left 0.3s ease;
    overflow-y: auto;
    display: flex !important;
  }

  .sidebar.mobile-open {
    left: 0;
  }

  /* Main */
  .main {
    margin-left: 0;
    width: 100%;
    padding: 72px 14px 20px;
  }

  /* Header */
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    margin-bottom: 22px;
  }

  .page-title {
    font-size: 22px;
    line-height: 1.2;
  }

  .page-sub {
    font-size: 12px;
  }

  .page-header > div:last-child {
    display: flex;
    flex-direction: column;
    gap: 10px !important;
    width: 100%;
  }

  /* Buttons */
  .btn {
    width: 100%;
    justify-content: center;
    min-height: 44px;
    padding: 12px;
    font-size: 13px;
  }

  /* Stats */
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 24px;
  }

  .stat-card {
    padding: 18px;
    border-radius: 14px;
  }

  .stat-value {
    font-size: 22px;
    line-height: 1.2;
  }

  .stat-label {
    font-size: 10px;
  }

  .stat-sub {
    font-size: 11px;
  }

  /* Table */
  .table-card {
    border-radius: 14px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    min-width: 720px;
  }

  th {
    font-size: 10px;
    padding: 10px 12px;
  }

  td {
    font-size: 12px;
    padding: 12px;
    white-space: nowrap;
  }

  /* Actions */
  .actions {
    display: flex;
    gap: 6px;
    flex-wrap: nowrap;
  }

  .action-btn {
    padding: 6px 10px;
    font-size: 10px;
    white-space: nowrap;
  }

  /* Sidebar Nav */
  .sidebar-nav {
    padding: 18px 12px;
  }

  .nav-item {
    font-size: 13px;
    padding: 11px 12px;
  }

  /* User Footer */
  .sidebar-footer {
    padding: 14px;
  }

  .user-name {
    font-size: 12px;
  }

  .user-plan {
    font-size: 10px;
  }

  .logout-btn {
    min-height: 42px;
  }

  /* Empty State */
  .empty-state {
    padding: 40px 18px;
  }

  .empty-icon {
    font-size: 34px;
  }

  .empty-title {
    font-size: 14px;
  }

  .empty-sub {
    font-size: 12px;
  }
}


/* =========================================
   SMALL PHONES
========================================= */

@media (max-width: 480px) {

  .main {
    padding: 70px 10px 16px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-sub {
    font-size: 11px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 20px;
  }

  .table-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .table-title {
    font-size: 13px;
  }

  .btn {
    font-size: 12px;
  }

  .action-btn {
    padding: 5px 8px;
    font-size: 9px;
  }

  .mobile-menu-btn {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
}
      `}</style>

      <div className="shell">
        {/* <aside className={`sidebar ${mounted ? "in" : ""}`}> */}
          <aside className={`sidebar ${mounted ? "in" : ""} ${mobileMenu ? "mobile-open" : ""}`}>
          <div className="sidebar-logo">
            <div className="mark">Invoice AI</div>
            <div className="sub">GST · AI · India</div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-item active"><span className="nav-icon">▦</span> Dashboard</div>
            <Link to="/" className="nav-item"><span className="nav-icon">✨</span> WelcomePage</Link>     
            <Link to="/invoices" className="nav-item"><span className="nav-icon">📄</span> Invoices</Link>
            <Link to="/invoice/new" className="nav-item"><span className="nav-icon">＋</span> New Invoice</Link>
            <Link to="/invoice/ai" className="nav-item"><span className="nav-icon">✦</span> AI Generate</Link>
            <Link to="/settings" className="nav-item"><span className="nav-icon">⚙</span> Settings</Link>
            <Link to="/billing" className="nav-item"><span className="nav-icon">💳</span> Billing</Link>
          </nav>
          <div className="sidebar-footer">
            <div className="user-row">
              <div className="user-avatar">
                {user?.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : (user?.name || "U")[0].toUpperCase()}
              </div>
              <div>
                <div className="user-name">{user?.name || "User"}</div>
                <div className="user-plan">{stats.plan || "free"} plan</div>
              </div>
            </div>
            {/* <button className="logout-btn" onClick={handleLogout}>Sign out</button> */}
            <button className="logout-btn" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? (
              <><span className="spinner" /> Signing out...</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
                </svg> &nbsp;
                Sign out
              </>
            )}
          </button>
          </div>
        </aside>
<button
  className="mobile-menu-btn"
  onClick={() => setMobileMenu(!mobileMenu)}
>
  ☰
</button>
<div
  className={`mobile-overlay ${mobileMenu ? "show" : ""}`}
  onClick={() => setMobileMenu(false)}
></div>
        <main className={`main ${mounted ? "in" : ""}`}>
          
          <div className="page-header">
            <div>
              <div className="page-title">Dashboard</div>
              <div className="page-sub">Overview of your invoicing activity</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ai" onClick={() => navigate("/invoice/ai")}>✦ AI Generate</button>
              <button className="btn btn-primary" onClick={() => navigate("/invoice/new")}>＋ New Invoice</button>
            </div>
          </div>

          {loading ? (
            <div className="spinner" />
          ) : (
            <>
              <div className="stats-grid">
                <div className="stat-card highlight">
                  <div className="stat-label">Total Revenue</div>
                  <div className="stat-value">{fmt(stats.totalRevenue)}</div>
                  <div className="stat-sub">From paid invoices</div>
                  <div className="usage-bar-wrap">
                    <div className="usage-bar-bg">
                      <div className="usage-bar-fill" style={{ width: `${Math.min(usagePercent, 100)}%` }} />
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Pending</div>
                  <div className="stat-value" style={{ color: "var(--yellow)" }}>{fmt(stats.pending)}</div>
                  <div className="stat-sub">Awaiting payment</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Invoices</div>
                  <div className="stat-value">{stats.total || 0}</div>
                  <div className="stat-sub">{stats.invoiceCount}/{stats.limit === Infinity ? "∞" : stats.limit} used</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Overdue</div>
                  <div className="stat-value" style={{ color: stats.overdue > 0 ? "var(--red)" : "var(--ink)" }}>{stats.overdue || 0}</div>
                  <div className="stat-sub">Need attention</div>
                </div>
              </div>

              <div className="table-card">
                <div className="table-header">
                  <div className="table-title">Recent Invoices</div>
                  <Link to="/invoices" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 14px" }}>View all →</Link>
                </div>
                {recent.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📄</div>
                    <div className="empty-title">No invoices yet</div>
                    <div className="empty-sub">Create your first GST invoice in seconds</div>
                    <button className="btn btn-primary" onClick={() => navigate("/invoice/new")}>＋ Create Invoice</button>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Client</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((inv) => (
                        <tr key={inv._id}>
                          <td><span className="invoice-num">{inv.invoiceNumber}</span></td>
                          <td><span className="client-name">{inv.client?.name}</span></td>
                          <td>{fmtDate(inv.issueDate)}</td>
                          <td><span className="amount">{fmt(inv.totalAmount)}</span></td>
                          <td>
                            <span className="status-badge" style={STATUS_COLORS[inv.status] || {}}>
                              {inv.status}
                            </span>
                          </td>
                          <td>
                            <div className="actions">
                              <button className="action-btn" onClick={() => navigate(`/invoice/${inv._id}`)}>View</button>
                              <button className="action-btn" onClick={() => handleDownload(inv._id, inv.invoiceNumber)}>PDF</button>
                              <button className="action-btn danger" onClick={() => handleDelete(inv._id)}>Del</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
}
